/**
 * High-performance in-memory permission cache
 * Uses window object to persist across components without re-fetching
 * TTL: 30 minutes to ensure fresh permissions
 */

const CACHE_KEY = '__ERP_MAF_PERMISSION_CACHE__';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CacheEntry {
  timestamp: number;
  data: any;
}

interface PermissionCacheData {
  userPermissions: Record<string, string>;
  accessibleModules: string[];
  currentUser: any;
  userProfile: any[];
  hierarchicalPermissions: Record<string, any>;
}

/**
 * Get the cache root from window object
 */
const getCacheRoot = (): Record<string, CacheEntry> => {
  if (typeof window === 'undefined') {
    return {};
  }
  if (!(window as any)[CACHE_KEY]) {
    (window as any)[CACHE_KEY] = {};
  }
  return (window as any)[CACHE_KEY];
};

/**
 * Read cached permissions for a user
 * Returns null if cache is expired or doesn't exist
 */
export const readCachedPermissions = (username: string): PermissionCacheData | null => {
  if (!username) return null;

  const root = getCacheRoot();
  const entry = root[username];

  if (!entry) return null;

  // Check if cache is expired
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    delete root[username];
    return null;
  }

  return entry.data;
};

/**
 * Write permissions to cache
 */
export const writeCachedPermissions = (username: string, data: PermissionCacheData): void => {
  if (!username) return;

  const root = getCacheRoot();
  root[username] = {
    timestamp: Date.now(),
    data,
  };
};

/**
 * Clear cached permissions for a specific user
 */
export const clearCachedPermissions = (username: string): void => {
  const root = getCacheRoot();
  if (username && root[username]) {
    delete root[username];
  }
};

/**
 * Clear all cached permissions
 */
export const clearAllCachedPermissions = (): void => {
  const root = getCacheRoot();
  Object.keys(root).forEach((key) => {
    delete root[key];
  });
};

/**
 * Check if cache exists and is valid for a user
 */
export const hasCachedPermissions = (username: string): boolean => {
  if (!username) return false;

  const root = getCacheRoot();
  const entry = root[username];

  if (!entry) return false;

  // Check if cache is expired
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    delete root[username];
    return false;
  }

  return true;
};
